'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  MANUAL_LIMITS,
  altitudeCommandFromRail,
  calibrateSurface,
  clampToGeofence,
  deadReckonPosition,
  flyToHereGate,
  hoverSetpoint,
  manualEnvelope,
  pointerFromTarget,
  ringGeometry,
  setpointFromPointer,
  surfaceFurniture,
  targetFromPointer,
  type LatLng,
  type ManualClampReason,
  type PointerMode,
} from '@/lib/manual-vector';
import {
  DEFAULT_MANUAL_RING_VISIBILITY,
  ringsPainted,
  type ManualRingVisibility,
} from '@/lib/manual-rings';
import {
  DEFAULT_MANUAL_HANDEDNESS,
  MANUAL_HAPTIC,
  MANUAL_TOUCH_NOTICE,
  manualControlAnchor,
  vibrate,
  type ManualHandedness,
} from '@/lib/manual-touch';
import { DEFAULT_MANUAL_STAGE_LAYOUT, type ManualStageLayout } from '@/lib/manual-layout';
import { useMeasuredSize } from '@/hooks/useMeasuredSize';
import { stageCentreShiftPx, stageInsetsForHost } from './CopStageInsets';
import { useStageInsets } from './useStageInsets';
import { DEFAULT_ALTITUDE_PROFILE, type AltitudeProfile } from '@/lib/altitude';
import type { ManualControlController } from '@/hooks/useManualControl';
import type { LiveDrone } from '@/hooks/useWebSocket';

/**
 * The map IS the control surface.
 *
 * The operator drags a velocity vector out of the aircraft symbol
 * (`docs/PLANS/manual-flight-control/README.md`): no on-screen joysticks, no
 * arrow pad. This component draws that surface over the map and turns the
 * gesture into frames; every number it derives comes from `lib/manual-vector.ts`
 * and every frame it sends goes through `useManualControl`. It owns neither the
 * maths nor the wire — only the pixels and the pointer.
 *
 * Six things about it are load-bearing:
 *
 * 1. **Only the controls take the pointer.** The container is
 *    `pointer-events: none` and exactly four elements opt back in: the grab
 *    halo, the yaw ring, the altitude knob and (when one exists) the target pin.
 *    A press anywhere else is a map pan, which is what an operator expects a map
 *    to do even while flying.
 * 2. **Release always means hover.** `pointerup`, `pointercancel` and
 *    `pointerleave` all end the gesture the same way, and so does a SECOND
 *    POINTER landing anywhere on the page; the hook adds `window.blur` and
 *    `visibilitychange` on top. There is no path through this component that
 *    leaves a vector latched, and no path where cancelling means "keep the last
 *    velocity".
 * 3. **One finger flies, two fingers pan.** A drag captures its pointer, so a
 *    finger that slips off the halo keeps flying. The moment a second pointer
 *    arrives the drag is cancelled to a hover FIRST and the capture is released,
 *    which hands the pinch to the map underneath with the aircraft already
 *    stopped. `pointercancel` — routine on a tablet, not an edge case: a system
 *    gesture, a notification, an incoming call — takes the same path.
 * 4. **The clamp is shown, not applied silently.** The commanded vector stops at
 *    the ring or short of the geofence while the pointer keeps travelling, and
 *    the pointer's own position stays drawn — so the operator sees the limit
 *    take effect rather than wondering why the aircraft is slower than the hand.
 * 5. **The keyboard is the same control.** `W/S/A/D/C/Z/Q/E` feed the identical
 *    composition path as the pointer, `F` drops a target pin, and that pin is
 *    nudged with the arrow keys. That is WCAG 2.1.1 (Keyboard) — a complete
 *    keyboard path to every gesture, and the only path at all for an operator
 *    who cannot drag, since GO-TO, the pointer fallback verb, is refused while
 *    manual authority is held.
 *
 *    It is NOT a claim of 2.5.7 (Dragging Movements), which asks for a
 *    SINGLE-POINTER, non-dragging alternative; a keyboard is not one. The
 *    honest answer for the vector itself is 2.5.7's "essential" exception: the
 *    gesture IS a continuously variable velocity — bearing and magnitude,
 *    updated frame by frame at the rate the aircraft is commanded — and a
 *    tap-and-step equivalent would be a different control commanding a
 *    different thing. Where a discrete alternative CAN exist it is provided
 *    anyway (the target pin can be created and refined without a drag).
 * 6. **The centre of a tablet is the least reachable part of it.** A tablet is
 *    held two-handed, so on a touch pointer the whole control moves into one
 *    bottom corner and STOP into the other (`manualControlAnchor`), and the map
 *    is asked to hold the aircraft under the new anchor rather than in the
 *    middle. On a mouse nothing moves: a cursor reaches everywhere equally.
 *
 * Everything this component draws is expressed in RING space (a fraction of
 * `outerRadiusPx`) relative to that anchor, never in map pixels, so a stale zoom
 * can misalign the basemap under the surface but can never misdraw the vector,
 * the ghost or the fence.
 *
 * That last property is what makes ONE surface serve all three stage layouts
 * (`lib/manual-layout.ts`). A 900 px map and a 280 px minimap differ only in
 * the zoom `calibrateSurface` asks for; the gesture, the ring and the clamp are
 * identical, and this component has no branch on layout anywhere in its
 * drawing. The two things layout DOES change are both about the camera rather
 * than the control: whether the operator may release the lock (`focusLocked`),
 * and whether a press outside the controls pans the map or is swallowed
 * (`lockPointer`, true only in the minimap).
 */

/** Gap between the outer speed ring and the yaw ring, CSS px. */
const YAW_RING_GAP_PX = 20;
/** Hit and paint width of the yaw ring, CSS px — well past the contract's 14 px floor. */
const YAW_RING_STROKE_PX = 20;
/**
 * How far the dark outline extends past each side of a ring stroke, CSS px.
 *
 * The rings are drawn over an ARBITRARY basemap — dark tiles, day tiles,
 * satellite imagery — so a semantic surface token cannot promise SC 1.4.11's
 * 3:1 against any of them. A light stroke bounded by a near-black outline holds
 * on all three, which is the same technique a map label uses for the same
 * reason. See `--manual-ring` / `--manual-ring-outline` in `globals.css`.
 */
const RING_OUTLINE_PX = 1.5;
/** Angular drag that reaches full yaw rate, degrees. */
const YAW_FULL_DEFLECTION_DEG = 90;
/** How often the ghost and the dead-man readout re-derive, ms. */
const TICK_MS = 500;
/**
 * Clearance kept between the outermost drawn ring and the surface edge when the
 * control is anchored in a corner, CSS px. The yaw ring is the outermost thing
 * drawn, and a ring half off the screen is a control the operator cannot see.
 */
const ANCHOR_EDGE_PAD_PX = 8;
/** Arrow-key step for refining a target pin, CSS px. Shift multiplies it. */
const PIN_NUDGE_PX = 4;
const PIN_NUDGE_COARSE_PX = 16;
/**
 * Drops a target pin without a pointer (WCAG 2.1.1, Keyboard).
 *
 * A long press is the only way to CREATE one with a pointer, and GO-TO — the
 * fallback verb — is refused while manual authority is held, so without this key
 * a keyboard-only operator holding the aircraft cannot command a fly-to at all.
 * The pin lands at the ring's north point and is then refined with the arrow
 * keys, which is the same refinement a dragged pin gets.
 *
 * The pin is also the one part of this surface with a genuine single-pointer,
 * non-dragging path (2.5.7): a second long press re-places it, so refining it
 * never REQUIRES the drag. The velocity vector has no such path, and does not
 * claim one — see the header.
 */
const FLY_TO_HERE_KEY = 'f';
/** Quantise to 3 dp toward zero — same rule the wire axes use. */
function trunc3(value: number): number {
  const quantised = Math.trunc(value * 1000) / 1000;
  return quantised === 0 ? 0 : quantised;
}

/** What the surface last told the map; the neutral value is "nothing was taken". */
interface AnnouncedFocus {
  droneId: string | null;
  sessionActive: boolean;
}

/** A surface holding neither the camera nor a session has nothing to give back. */
function announcesNothing(a: AnnouncedFocus): boolean {
  return a.droneId === null && !a.sessionActive;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** `speed` → `SPEED`. The RC's clamp vocabulary, rendered as words. */
const CLAMP_WORD: Record<ManualClampReason, string> = {
  speed: 'SPEED',
  vertical: 'VERTICAL',
  // The RC emits this as its own word, never folded into `speed`: a yaw-rate
  // clamp reported as a horizontal-speed clamp writes a limit breach that never
  // happened into the audit trail (WIRE-CONTRACT.md, `RC → COP state`).
  yaw: 'YAW',
  ceiling: 'CEILING',
  floor: 'FLOOR',
  geofence: 'GEOFENCE',
  battery: 'BATTERY',
};

/**
 * A `FLY TO HERE` target, ready to be confirmed and dispatched.
 *
 * `lat`/`lng` are the ground truth and the only thing the wire ever sees; every
 * other field is what the operator is owed BEFORE they confirm — how far, which
 * way, how high, how fast, and how precise the tap could possibly have been at
 * this scale. `uncertaintyLabel` comes from `flyToHereGate`, so it tracks the
 * real scale of the real surface instead of being a literal that goes stale the
 * first time a layout changes.
 */
export interface ManualFlyTarget extends LatLng {
  /** Ground distance from the aircraft, m. */
  distanceM: number;
  /** Bearing from the aircraft, degrees true; null only if it coincides. */
  bearingDeg: number | null;
  /** `±3 M AT THIS ZOOM`, computed by `flyToHereGate`. */
  uncertaintyLabel: string;
  uncertaintyM: number;
  /** The altitude the GO-TO will fly, m AGL — answered by the altitude rail. */
  altitudeM: number;
  /** The speed it will fly, m/s — the operator's speed preset. */
  speedMs: number;
}

export interface ManualMapControlProps {
  drone: LiveDrone;
  manual: ManualControlController;
  /**
   * Keep-in boundary the 4-second projection is clamped against, or null. A
   * hint to the operator only — the hard envelope is enforced on the RC.
   */
  keepIn?: GeoJSON.Geometry | null;
  /**
   * Compass bearing of screen-up. 0 for a north-up map.
   *
   * In the COP this stays 0 because the map REFUSES to rotate for the duration
   * of a manual session (`setManualRotationLock` in `DroneMap`): a right-drag or
   * a two-finger twist during the 8 s the camera lock is released would turn the
   * basemap while this surface still read screen-up as north, and the drawn
   * vector, the BRG readout and the aircraft's actual track would diverge by
   * exactly that rotation with nothing on screen saying so. The geometry carries
   * the rotation anyway, so a host that does allow one is correct by passing it.
   */
  mapBearingDeg?: number;
  /** Measured, not guessed — see `useManualDevice`. Every constant follows it. */
  pointer?: PointerMode;
  /** Which bottom corner the control takes on a touch pointer. */
  handed?: ManualHandedness;
  /**
   * The device is held upright. There is no portrait control layout, so the
   * surface shows a rotate prompt instead of one — and stops the aircraft.
   */
  portrait?: boolean;
  /** Org altitude profile — the ceiling the rail clamps against. */
  altitudeProfile?: AltitudeProfile;
  /**
   * Which stage layout is hosting the surface. Nothing about the gesture
   * depends on it — but a CHANGE of it is a change of scale, so any drag in
   * flight is cancelled rather than carried across, and so is any target pin.
   */
  layout?: ManualStageLayout;
  /**
   * False when the operator has taken the camera back. The surface stops asking
   * the map to hold the aircraft, and offers RECENTER instead.
   */
  focusLocked?: boolean;
  /** Put the camera back on the aircraft. Absent → no RECENTER control. */
  onRecenter?: () => void;
  /**
   * Swallow every press that is not one of the controls, so the map under the
   * surface cannot be panned. True in the control minimap only: there the
   * surface IS the control, and a map that scrolled out from under the aircraft
   * symbol would be a control with no fixed centre.
   */
  lockPointer?: boolean;
  /**
   * The pending `FLY TO HERE` target, or null. Held above this component so the
   * confirm bar can live OUTSIDE the map — a dialog over the map would hide
   * exactly what the operator has to look at before confirming.
   */
  flyTarget?: ManualFlyTarget | null;
  /**
   * A target was placed or refined, or cleared (null). Absent → no long press
   * and no `F` key.
   *
   * There is deliberately no "dispatch it straight away" partner to this. Every
   * FLY TO HERE goes through the confirm bar at every scale — see
   * `flyToHereGate` for what the old scale rule actually did.
   */
  onFlyTarget?: (target: ManualFlyTarget | null) => void;
  /**
   * Whether the calibration rings are painted at rest.
   *
   * Display only. The geometry is computed and the vector clamped against the
   * same radii either way — see `manual-rings.ts`. `active` still paints them
   * for the duration of a drag, a yaw or a held flight key, because the
   * graduations are what a speed is aimed at.
   */
  ringVisibility?: ManualRingVisibility;
  /** Injected clock, for tests. */
  now?: number;
}

export function ManualMapControl({
  drone,
  manual,
  keepIn = null,
  mapBearingDeg = 0,
  pointer = 'desktop',
  handed = DEFAULT_MANUAL_HANDEDNESS,
  portrait = false,
  altitudeProfile = DEFAULT_ALTITUDE_PROFILE,
  layout = DEFAULT_MANUAL_STAGE_LAYOUT,
  focusLocked = true,
  onRecenter,
  lockPointer = false,
  ringVisibility = DEFAULT_MANUAL_RING_VISIBILITY,
  flyTarget = null,
  onFlyTarget,
  now,
}: ManualMapControlProps) {
  // The surface size decides whether this is a control at all, so it is measured
  // rather than assumed — the same hook the COP measures the map column with, so
  // "too small to fly from" is one answer and not two.
  /**
   * What the tactical shell's floating chrome is covering, and therefore where
   * this surface may be drawn.
   *
   * All zeroes in the classic shell, so the surface is `inset-0` exactly as it
   * has always been. In the tactical shell the map canvas is deliberately larger
   * than the visible map, and a control drawn to the canvas would put its ring -
   * and on a touch pointer its grab halo, in the handed bottom corner - underneath
   * the command bar. Insetting the surface instead means `ringGeometry` and
   * `manualControlAnchor` below are answering about the map the operator can
   * actually see, with no second copy of the arithmetic.
   *
   * Not applied in the VIDEO layout: there the surface is hosted inside the
   * 280 px control minimap, which is its own box and already clear of the stack.
   */
  const stageInsets = useStageInsets();
  const surfaceInsets = stageInsetsForHost(layout === 'video', stageInsets);

  const { ref: surfaceRef, size } = useMeasuredSize<HTMLDivElement>();
  // Memoised so an unmeasured surface does not hand a fresh object to the ring
  // on every render, which would re-derive the calibration (and re-announce the
  // camera) for a size that has not changed.
  const surface = useMemo(() => size ?? { widthPx: 0, heightPx: 0 }, [size]);

  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (now !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [now]);
  const clockMs = now ?? tick;

  const envelope = useMemo(
    () => manual.envelope ?? manualEnvelope(altitudeProfile),
    [manual.envelope, altitudeProfile],
  );

  const ring = useMemo(
    () => ringGeometry(surface, { pointer, maxSpeedMs: envelope.max_speed_ms }),
    [surface, pointer, envelope.max_speed_ms],
  );
  const calibration = useMemo(
    () => calibrateSurface(ring, drone.latitude),
    [ring, drone.latitude],
  );

  /**
   * Where the control sits. Centre on a mouse; the handed bottom corner on a
   * finger, because a two-handed grip makes the middle of the screen the least
   * reachable place on it. The margin is the OUTERMOST thing drawn — the yaw
   * ring, which lives outside the speed ring — so no part of the control can be
   * pushed off the surface.
   */
  const anchor = useMemo(
    () =>
      manualControlAnchor(surface, {
        pointer,
        handed,
        marginPx: ring.outerRadiusPx + YAW_RING_GAP_PX + YAW_RING_STROKE_PX / 2 + ANCHOR_EDGE_PAD_PX,
      }),
    [surface, pointer, handed, ring.outerRadiusPx],
  );
  const cx = anchor.xPx;
  const cy = anchor.yPx;
  // Read from pointer handlers, which outlive the render that created them.
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;

  const interactive = manual.holdsAuthority && ring.controllable && !portrait;

  /**
   * The boxes drawn AROUND the ring — the readout and the altitude rail.
   *
   * They do not scale with the surface, so at minimap size they collide with
   * STOP and with the edges. `surfaceFurniture` is where that arithmetic lives,
   * and RECENTER is fed in because it claims the top of the surface for as long
   * as it exists.
   */
  const recenterShown = manual.holdsAuthority && !focusLocked && onRecenter !== undefined;
  const furniture = useMemo(
    () => surfaceFurniture(surface, { pointer, recenterShown }),
    [surface, pointer, recenterShown],
  );

  // ---------------------------------------------------------------------------
  // Ask the map to hold the aircraft under the anchor at the calibrated zoom.
  //
  // Sent on the same window-event channel the COP already uses to drive the map
  // camera from outside it (`flyToDrone`, `flyToSite` in DroneMap). Manual mode
  // needs it because the grab halo is drawn ON the aircraft symbol: that is only
  // true while the aircraft sits under the anchor, and the ring only means "four
  // seconds of travel" at this zoom. `offsetXPx`/`offsetYPx` are what let the
  // control leave the centre of the screen without the halo leaving the symbol.
  //
  // `droneId: null` is the CAMERA release. What the operator gets back is drawn
  // here — a RECENTER control — and an 8-second timer above this component
  // re-locks on its own, so a released camera repairs itself.
  //
  // `sessionActive` is a SEPARATE fact on the same channel, and the two must not
  // be collapsed back together. "Stop following the aircraft" and "nobody is
  // flying any more" look identical from the camera's point of view, but they are
  // opposite answers to the question the map actually needs: may the operator
  // rotate or pitch the basemap? While a session is live the answer is no even
  // when the camera has been let go, because the surface is still converting
  // screen-up to a compass bearing and still converting a FLY TO HERE press to
  // ground metres with a flat scale. Sending only the camera fact would hand
  // rotation back for the 8 seconds of every look, and fly the aircraft off by
  // whatever the operator twisted the map to. See `setManualRotationLock`.
  //
  // The session is live when authority is held AND the surface is big enough to
  // fly from: a display-only surface commands nothing, so it has no bearing to
  // get wrong.
  // ---------------------------------------------------------------------------
  const focusCentreShift = useMemo(() => stageCentreShiftPx(surfaceInsets), [surfaceInsets]);

  const focusDetail = useMemo(() => {
    const sessionActive = manual.holdsAuthority && ring.controllable;
    return sessionActive && focusLocked
      ? {
          droneId: drone.drone_id,
          sessionActive,
          zoom: calibration.zoom,
          // Two different centres, reconciled here. `anchor.offset*` is measured
          // from the centre of THIS SURFACE; MapLibre's `easeTo({ offset })` is
          // measured from the centre of the map CONTAINER. In the classic shell
          // they are the same box and the shift is zero. In the tactical shell
          // the surface is inset by the floating chrome, so without this the
          // grab halo lands off the aircraft symbol by exactly half the inset -
          // which is the whole failure this shift exists to prevent.
          offsetXPx: anchor.offsetXPx + focusCentreShift.dxPx,
          offsetYPx: anchor.offsetYPx + focusCentreShift.dyPx,
        }
      : { droneId: null, sessionActive };
  }, [
    manual.holdsAuthority,
    ring.controllable,
    focusLocked,
    drone.drone_id,
    calibration.zoom,
    anchor.offsetXPx,
    anchor.offsetYPx,
    focusCentreShift.dxPx,
    focusCentreShift.dyPx,
  ]);
  // What this surface last told the map, so it only ever releases something it
  // took. The first render has not been measured yet and is therefore neither
  // controllable nor a session — announcing anything there would tell the map to
  // drop a lock that some other surface might legitimately hold.
  //
  // Both facts are tracked, not just the camera one: a session that ends while
  // the camera is already released changes ONLY `sessionActive`, and that is the
  // event that gives rotation back. Keying the guard on `droneId` alone would
  // swallow it and leave the map permanently unable to rotate.
  const announcedRef = useRef<AnnouncedFocus>({ droneId: null, sessionActive: false });
  useEffect(() => {
    if (announcesNothing(focusDetail) && announcesNothing(announcedRef.current)) return;
    announcedRef.current = {
      droneId: focusDetail.droneId,
      sessionActive: focusDetail.sessionActive,
    };
    window.dispatchEvent(new CustomEvent('manualFocus', { detail: focusDetail }));
  }, [focusDetail]);
  // Unmount only — a separate effect so a change of zoom or of lock state does
  // not fire a release the map would have to undo a microtask later. This is the
  // route every session end actually takes: the surface is rendered only while
  // `holdsAuthority` is true, so an operator release, the dead-man, an override
  // and a deselect all arrive here as an unmount.
  useEffect(
    () => () => {
      if (announcesNothing(announcedRef.current)) return;
      announcedRef.current = { droneId: null, sessionActive: false };
      window.dispatchEvent(
        new CustomEvent('manualFocus', { detail: { droneId: null, sessionActive: false } }),
      );
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Pointer state. Four independent gestures, one pointer id each.
  // ---------------------------------------------------------------------------
  const [vectorDrag, setVectorDrag] = useState<{ dxPx: number; dyPx: number } | null>(null);
  const [yawDrag, setYawDrag] = useState<number | null>(null);
  const [railDrag, setRailDrag] = useState<number | null>(null);
  const [pinDragging, setPinDragging] = useState(false);
  const vectorPointerRef = useRef<number | null>(null);
  const yawPointerRef = useRef<number | null>(null);
  const railPointerRef = useRef<number | null>(null);
  const pinPointerRef = useRef<number | null>(null);
  /**
   * The yaw sweep so far, degrees, and the angle the last move left the
   * pointer at.
   *
   * Deliberately NOT the angle the gesture started at. `signedAngleDelta`
   * answers in −180..180, so measuring the whole drag against a fixed start
   * angle wraps at the antipode: one continuous sweep past the halfway point
   * reads as a sweep the other way, and the commanded yaw inverts mid-drag and
   * stays inverted. A single move never covers half a turn, so accumulating
   * per-move deltas keeps the sign of the sweep for as long as it lasts.
   */
  const yawSweepRef = useRef(0);
  const yawPrevAngleRef = useRef(0);
  /**
   * The element and pointer id currently captured, so a cancellation can hand
   * the pointer back. Without the release, finger one stays routed to the halo
   * and the map underneath sees a one-finger gesture where the operator made a
   * two-finger one.
   */
  const capturedRef = useRef<{ element: Element; pointerId: number } | null>(null);

  /**
   * Forget every gesture in flight, WITHOUT touching the wire.
   *
   * Safe to call during render (React allows a `setState` on the component being
   * rendered), which is what both callers below need — and why it must not send:
   * the send effect owns the wire, and it hovers on the same transitions.
   */
  const forgetGestures = () => {
    vectorPointerRef.current = null;
    yawPointerRef.current = null;
    yawSweepRef.current = 0;
    railPointerRef.current = null;
    pinPointerRef.current = null;
    setVectorDrag(null);
    setYawDrag(null);
    setRailDrag(null);
    setPinDragging(false);
  };

  // -------------------------------------------------------------------------
  // A change of layout is a change of SCALE. Never carry a vector across one.
  //
  // Adjusted during render rather than in an effect (the pattern
  // `ManualFlightBanner` uses for its ttl sample) because an effect would run
  // AFTER the send effect below in the same commit — so one frame at the new
  // scale would go out, derived from a pointer offset measured at the old one.
  // Cleared here, the very first thing the new scale sees is a hover.
  //
  // The wire-level half of this is in the switcher itself, which calls `hover()`
  // before it changes anything; this is the local half, and neither is
  // sufficient alone. A pending target pin goes too: it was placed against a
  // scale that no longer exists, and its `±N M AT THIS ZOOM` was measured there.
  // -------------------------------------------------------------------------
  const [renderedLayout, setRenderedLayout] = useState(layout);
  if (renderedLayout !== layout) {
    setRenderedLayout(layout);
    forgetGestures();
  }

  // -------------------------------------------------------------------------
  // The surface stopped being a control mid-gesture.
  //
  // A tablet turned upright, or a details panel narrowing the map column past
  // the minimum: the send effect below hovers the aircraft, but the DRAG STATE
  // would survive, because the elements carrying the pointer handlers are
  // unmounted by the same render and their `pointerup` never arrives. Rotating
  // back would then re-run the send effect with `vectorDrag` still set and
  // resume flight with nobody's hand on the control.
  //
  // Cleared during render for the same reason the layout switch is: an effect
  // would run after the send effect and let that one frame out first.
  // -------------------------------------------------------------------------
  const [renderedInteractive, setRenderedInteractive] = useState(interactive);
  if (renderedInteractive !== interactive) {
    setRenderedInteractive(interactive);
    if (!interactive) forgetGestures();
  }

  // -------------------------------------------------------------------------
  // STOP DISARMS THE POINTER, not just the wire.
  //
  // Space is the documented stop, and it reaches `manual.hover()` — which
  // clears the held flight keys, cancels the pending frame and stops the
  // aircraft. It cannot clear a DRAG, because the drag lives here. So the
  // aircraft stopped and the control stayed armed: the next pointermove of one
  // pixel recomputed the same setpoint, the send effect below saw its deps
  // change, and full commanded flight resumed with no new deliberate gesture.
  // Keys disarmed and pointer not, on the panic key. Same shape for the blur
  // and visibility hovers when `pointercancel` never arrives.
  //
  // The counter is bumped by EVERY hover, wherever it came from, so this is one
  // rule and not a list of stop paths to keep in step.
  //
  // Cleared during render, like the two blocks above and for the same reason: a
  // stop that also clears the flight keys changes this render's vertical and
  // yaw, so the send effect DOES re-run in that commit — and an effect here
  // would run too late to stop it going out with the drag still in it. Past
  // this, the first thing that effect can see is a hover.
  // -------------------------------------------------------------------------
  const [disarmedAt, setDisarmedAt] = useState(manual.stopSignal);
  if (disarmedAt !== manual.stopSignal) {
    setDisarmedAt(manual.stopSignal);
    forgetGestures();
  }

  const onFlyTargetRef = useRef(onFlyTarget);
  onFlyTargetRef.current = onFlyTarget;
  // Compared against the previous value rather than run on every commit, so
  // MOUNTING the surface does not clear a target: an effect that fired on the
  // first render would drop the pin the moment a re-host handed the surface a
  // new box, which is exactly when the operator is still looking at it.
  const clearedForLayoutRef = useRef(layout);
  useEffect(() => {
    if (clearedForLayoutRef.current === layout) return;
    clearedForLayoutRef.current = layout;
    onFlyTargetRef.current?.(null);
  }, [layout]);

  // The RC says a human took the sticks. Whatever was pending here is no longer
  // this operator's to send.
  const pilotOverride = manual.pilotOverride;
  useEffect(() => {
    if (pilotOverride) onFlyTargetRef.current?.(null);
  }, [pilotOverride]);

  /** Client coordinates of the aircraft symbol — the origin of every gesture. */
  const centreOf = useCallback((): { x: number; y: number } => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: rect.left + anchorRef.current.xPx, y: rect.top + anchorRef.current.yPx };
  }, [surfaceRef]);

  const capture = (event: ReactPointerEvent<Element>) => {
    // A press on a control is never also a map pan, a text selection or a click
    // on whatever is underneath.
    event.preventDefault();
    event.stopPropagation();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
      capturedRef.current = { element: event.currentTarget, pointerId: event.pointerId };
    } catch {
      // Capture can be refused for a pointer that is already gone. The gesture
      // still works — the handlers are on the element either way — it just is
      // not contained if the pointer leaves it, which `onPointerLeave` covers.
      capturedRef.current = null;
    }
  };

  /** Give the pointer back to the page, so the map can take the next gesture. */
  const releaseCapture = useCallback(() => {
    const held = capturedRef.current;
    capturedRef.current = null;
    if (!held) return;
    try {
      held.element.releasePointerCapture(held.pointerId);
    } catch {
      // The pointer is already gone; there is nothing left to hand back.
    }
  }, []);

  /**
   * Every end of every gesture funnels here, and every one of them hovers.
   *
   * There is deliberately no "keep the last velocity" path: a cancellation is
   * the operator losing the control, not asking the aircraft to carry on.
   */
  const endGesture = useCallback(() => {
    releaseCapture();
    vectorPointerRef.current = null;
    yawPointerRef.current = null;
    yawSweepRef.current = 0;
    railPointerRef.current = null;
    pinPointerRef.current = null;
    setVectorDrag(null);
    setYawDrag(null);
    setRailDrag(null);
    setPinDragging(false);
    manual.hover();
  }, [manual, releaseCapture]);

  // The other half of the disarm above: hand the pointer back to the page.
  // Split out because `releasePointerCapture` is a DOM mutation and render is
  // not the place for one — and unlike the drag state it cannot let a frame
  // out, so a commit later is soon enough. Without it the halo keeps routing
  // the pointer it captured until the operator lifts it, and the map underneath
  // sees nothing.
  const disarmedCaptureRef = useRef(disarmedAt);
  useEffect(() => {
    if (disarmedCaptureRef.current === disarmedAt) return;
    disarmedCaptureRef.current = disarmedAt;
    releaseCapture();
  }, [disarmedAt, releaseCapture]);

  const onVectorDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!interactive || !event.isPrimary || event.button !== 0) return;
    capture(event);
    vectorPointerRef.current = event.pointerId;
    setVectorDrag({ dxPx: 0, dyPx: 0 });
  };
  const onVectorMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (vectorPointerRef.current !== event.pointerId) return;
    const centre = centreOf();
    setVectorDrag({ dxPx: event.clientX - centre.x, dyPx: event.clientY - centre.y });
  };
  const onVectorEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (vectorPointerRef.current !== event.pointerId) return;
    endGesture();
  };

  const onYawDown = (event: ReactPointerEvent<SVGCircleElement>) => {
    if (!interactive || !event.isPrimary || event.button !== 0) return;
    capture(event);
    yawPointerRef.current = event.pointerId;
    const centre = centreOf();
    yawPrevAngleRef.current = angleDeg(event.clientX - centre.x, event.clientY - centre.y);
    yawSweepRef.current = 0;
    setYawDrag(0);
  };
  const onYawMove = (event: ReactPointerEvent<SVGCircleElement>) => {
    if (yawPointerRef.current !== event.pointerId) return;
    const centre = centreOf();
    const angle = angleDeg(event.clientX - centre.x, event.clientY - centre.y);
    const sweep = advanceYawSweep(yawSweepRef.current, yawPrevAngleRef.current, angle);
    yawSweepRef.current = sweep;
    yawPrevAngleRef.current = angle;
    setYawDrag(sweep / YAW_FULL_DEFLECTION_DEG);
  };
  const onYawEnd = (event: ReactPointerEvent<SVGCircleElement>) => {
    if (yawPointerRef.current !== event.pointerId) return;
    endGesture();
  };

  const railTravelPx = furniture.railTravelPx;
  const railStartRef = useRef(0);
  const onRailDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!interactive || !event.isPrimary || event.button !== 0) return;
    capture(event);
    railPointerRef.current = event.pointerId;
    railStartRef.current = event.clientY;
    setRailDrag(0);
  };
  const onRailMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (railPointerRef.current !== event.pointerId) return;
    setRailDrag(clamp(event.clientY - railStartRef.current, -railTravelPx, railTravelPx));
  };
  const onRailEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (railPointerRef.current !== event.pointerId) return;
    endGesture();
  };

  // ---------------------------------------------------------------------------
  // Composition: pointer wins over keyboard, per axis.
  // ---------------------------------------------------------------------------

  /**
   * The flight keys as a synthetic pointer offset, so keyboard and pointer share
   * one code path and one clamp.
   *
   * Normalised to unit length BEFORE scaling to the ring: W+D pressed together
   * is full deflection on the diagonal, and feeding √2 into the ring would land
   * outside it and report a speed clamp the operator never caused.
   */
  const keyOffset = useMemo(() => {
    const { forward, right } = manual.keyAxes;
    if (forward === 0 && right === 0) return null;
    const length = Math.hypot(right, forward);
    return {
      dxPx: (right / length) * ring.outerRadiusPx,
      dyPx: (-forward / length) * ring.outerRadiusPx,
    };
  }, [manual.keyAxes, ring.outerRadiusPx]);

  const activeOffset = vectorDrag ?? keyOffset;

  const commanded = useMemo(() => {
    const base = activeOffset
      ? setpointFromPointer(activeOffset, ring, {
          frame: 'map',
          mapBearingDeg,
          speedCapMs: manual.speedCapMs,
        })
      : hoverSetpoint('map', ring.maxSpeedMs);
    return clampToGeofence(base, { lat: drone.latitude, lng: drone.longitude }, keepIn);
  }, [
    activeOffset,
    ring,
    mapBearingDeg,
    manual.speedCapMs,
    drone.latitude,
    drone.longitude,
    keepIn,
  ]);
  const setpoint = commanded.setpoint;

  const railOffsetPx =
    railDrag ?? (manual.keyAxes.up !== 0 ? -manual.keyAxes.up * railTravelPx : 0);
  const vertical = useMemo(
    () =>
      altitudeCommandFromRail({
        offsetPx: railOffsetPx,
        travelPx: railTravelPx,
        altitudeM: drone.altitude,
        envelope,
        pointer,
      }),
    [railOffsetPx, railTravelPx, drone.altitude, envelope, pointer],
  );

  const yawRate = trunc3(yawDrag ?? manual.keyAxes.yaw);

  /**
   * Paint the graduations?
   *
   * "Commanding" is any live input — a vector drag or a held flight key (both
   * arrive as `activeOffset`) or a yaw. Deliberately NOT `interactive`, which
   * is merely "could command": a surface the operator is only watching is
   * exactly the resting map this preference exists to clear.
   */
  const inputActive = activeOffset !== null || yawRate !== 0;
  const showRings = ringsPainted(ringVisibility, inputActive);

  // One send per changed frame. The hook coalesces to the control plane's rate
  // ceiling and re-sends to renew the ttl, so this can fire as often as the
  // pointer moves without any throttling of its own.
  //
  // The `!interactive` branch is not a no-op: the hook keeps renewing the last
  // setpoint until something replaces it, so a surface that stops being a
  // control — the details panel narrowing the map column below the minimum, the
  // tablet turned upright — must stop the aircraft rather than leave it flying a
  // vector nobody can see or cancel.
  const send = manual.send;
  const hoverNow = manual.hover;
  const holdsAuthority = manual.holdsAuthority;
  useEffect(() => {
    if (!holdsAuthority) return;
    if (!interactive) {
      hoverNow();
      return;
    }
    send({
      vx: setpoint.vx,
      vy: setpoint.vy,
      vz: vertical.vz,
      yawRate,
      frame: 'map',
      ttlMs: MANUAL_LIMITS.TTL_HOLD_MS,
    });
  }, [holdsAuthority, interactive, send, hoverNow, setpoint.vx, setpoint.vy, vertical.vz, yawRate]);

  // ---------------------------------------------------------------------------
  // Two fingers are the map's gesture, not ours.
  //
  // The second pointer can land anywhere on the page — the map canvas, the video
  // panel, another control — so this listens on the window in the CAPTURE phase
  // rather than on the surface. The drag is cancelled to a hover first and its
  // capture released, in that order: the aircraft is stopped before the pinch is
  // handed over, never after.
  // ---------------------------------------------------------------------------
  const gestureActive = vectorDrag !== null || yawDrag !== null || railDrag !== null || pinDragging;
  useEffect(() => {
    if (!gestureActive) return;
    const onSecondPointer = (event: PointerEvent) => {
      const held = capturedRef.current;
      if (held !== null && event.pointerId === held.pointerId) return;
      endGesture();
    };
    window.addEventListener('pointerdown', onSecondPointer, true);
    return () => window.removeEventListener('pointerdown', onSecondPointer, true);
  }, [gestureActive, endGesture]);

  // ---------------------------------------------------------------------------
  // Derived geometry for the drawing, all in ring space
  // ---------------------------------------------------------------------------
  const travelPx = Math.max(1, ring.outerRadiusPx - ring.deadZonePx);
  const vectorLengthPx = setpoint.isHover
    ? 0
    : ring.deadZonePx + setpoint.magnitude * travelPx;
  const screenBearingRad = setpoint.bearingDeg === null
    ? 0
    : ((setpoint.bearingDeg - mapBearingDeg) * Math.PI) / 180;
  const vectorTip = {
    x: cx + Math.sin(screenBearingRad) * vectorLengthPx,
    y: cy - Math.cos(screenBearingRad) * vectorLengthPx,
  };

  /** Metres → ring pixels. The identity the whole surface rests on. */
  const metresToRingPx = useCallback(
    (metres: number) => (metres / Math.max(1e-6, ring.ringRadiusM)) * ring.outerRadiusPx,
    [ring.ringRadiusM, ring.outerRadiusPx],
  );

  const ghost = useMemo(() => {
    const ageMs = clockMs - Date.parse(drone.last_seen_at);
    return deadReckonPosition(
      { lat: drone.latitude, lng: drone.longitude, ageMs: Number.isFinite(ageMs) ? ageMs : 0 },
      { groundSpeedMs: drone.speed ?? 0, courseDeg: drone.heading ?? 0 },
    );
  }, [clockMs, drone.last_seen_at, drone.latitude, drone.longitude, drone.speed, drone.heading]);

  const ghostRad = (((drone.heading ?? 0) - mapBearingDeg) * Math.PI) / 180;
  const ghostPx = metresToRingPx(ghost.advancedM);
  /**
   * How old the fix this whole surface is drawn from is, whole seconds.
   *
   * Everything on the control derives from ONE position: the aircraft symbol and
   * the grab halo drawn on it, the ghost, `clampToGeofence`'s keep-in distance
   * and red fence mark, and the origin a FLY TO HERE pin is resolved against. At
   * 10 s of telemetry loss all five freeze together and every one of them still
   * looks live — same green vector, same `SPD 5.0 m/s`, same green banner.
   *
   * `manualControlAvailability` refuses to START a session on a fix this old.
   * Once running it deliberately does NOT refuse (`useManualControl` — the RC's
   * dead-man ends a session, not a label on a screen), so the only thing left to
   * do is SAY SO, in words, where the operator is already looking.
   */
  // FLOOR, not round: the printed figure and the amber then cross at the same
  // instant. `TELEMETRY_STALE_MS` is 3 s, so the readout turns amber exactly as
  // it first shows `FIX +3s`, and a half-second-old fix reads `+0s` rather than
  // being aged up to a second it has not lived.
  const fixAgeS = Math.floor(Math.max(0, ghost.ageMs) / 1000);

  // Where the boundary crosses the commanded bearing. Drawn so a shortened
  // vector has a visible cause rather than just being mysteriously slow.
  const fenceMarkPx =
    commanded.distanceToEdgeM === null ? null : metresToRingPx(commanded.distanceToEdgeM);

  const clampWords = useMemo(() => {
    const words = new Set<string>();
    for (const reason of setpoint.clamped) words.add(CLAMP_WORD[reason]);
    if (vertical.clamped) words.add(CLAMP_WORD[vertical.clamped]);
    // The RC's own clamps outrank ours: ours is a prediction, its is what
    // actually happened to the aircraft.
    for (const reason of manual.clamped) words.add(CLAMP_WORD[reason] ?? reason.toUpperCase());
    return [...words];
  }, [setpoint.clamped, vertical.clamped, manual.clamped]);

  const callsign = drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase();

  // Nothing to draw until the surface has been measured; drawing at 0×0 would
  // put a ring at the corner for a frame.
  const measured = surface.widthPx > 0 && surface.heightPx > 0;

  /**
   * Everything this surface announces, and nothing it does not.
   *
   * The rule is DISCRETE CHANGE. A limit taking effect, the aircraft leaving the
   * keep-in, the surface refusing to be a control: each of those is a state the
   * operator has to hear once. A bearing is not — it changes at pointer rate,
   * and announcing it drowns the three that matter, which is exactly what an
   * `aria-live` on the readout panel used to do.
   *
   * The text is identical between renders unless one of those states changed, so
   * a re-render on its own produces no DOM mutation and therefore no
   * announcement.
   */
  const announcement = useMemo(() => {
    if (!measured) return '';
    if (portrait) return `${MANUAL_TOUCH_NOTICE.rotateTitle}. ${MANUAL_TOUCH_NOTICE.rotateDetail}`;
    // Sentences, not the panel's shorthand. Spoken once each, they have to
    // stand on their own without the readout beside them.
    if (!ring.controllable) return `Manual flight is display only. ${ring.refusal ?? ''}`;
    const parts: string[] = [];
    // First, because it is about whether ANY of the rest is current. Worded
    // without the number on purpose: the age ticks twice a second and the rule
    // for this region is DISCRETE CHANGE, so the sentence is keyed to the
    // threshold crossing and spoken once. The figure itself is in the readout.
    if (ghost.stale) {
      parts.push(
        `Telemetry is stale — this surface is drawn from a fix older than ${
          MANUAL_LIMITS.TELEMETRY_STALE_MS / 1000
        } seconds. The vector is still live; fly to here is refused until a fresh fix arrives.`,
      );
    }
    if (recenterShown) parts.push('Map unlocked — the camera re-locks on its own.');
    if (commanded.outside) {
      parts.push('The aircraft is outside the keep-in boundary — the vector is not clamped.');
    }
    if (clampWords.length > 0) parts.push(`Clamped by ${clampWords.join(', ')}.`);
    return parts.join(' ');
  }, [
    measured,
    portrait,
    ring.controllable,
    ring.refusal,
    ghost.stale,
    recenterShown,
    commanded.outside,
    clampWords,
  ]);

  // ---------------------------------------------------------------------------
  // FLY TO HERE
  //
  // ALWAYS confirmed, at every scale. Nothing this surface does dispatches a
  // real GO-TO straight out of a gesture: the press proposes, the bar states the
  // distance, the altitude, the speed and the `±N M AT THIS ZOOM` this scale is
  // good for, and only CONFIRM sends.
  //
  // It used to be conditional — confirmed below 150 m of visible ground,
  // dispatched directly above it. That rule was backwards in practice: metres
  // per pixel come from the RING (`ringRadiusM / outerRadiusPx`, ~0.15 on a
  // desktop ring), so 150 m of visible ground meant a surface under ~990 px, and
  // the DEFAULT full-map layout on any normal monitor therefore dispatched a
  // 500 ms press immediately — no arming, no confirmation, and no mention of the
  // gesture anywhere on a desktop surface. `flyToHereGate` still computes the
  // uncertainty, which is the point of the bar; it no longer decides whether the
  // operator gets one.
  // ---------------------------------------------------------------------------
  const gate = useMemo(
    () => flyToHereGate(surface.widthPx, calibration.metresPerPixel, pointer),
    [surface.widthPx, calibration.metresPerPixel, pointer],
  );

  /**
   * The altitude a target will be flown at.
   *
   * Answered by the altitude rail BEFORE the press, the same way
   * `GotoAltitudeBar` answers altitude before a map click: the operator climbs
   * or descends to the height they want with the rail, and FLY TO HERE holds
   * it. Clamped into the granted envelope so what the bar states is what the
   * backend will accept.
   */
  const targetAltitudeM = clamp(drone.altitude, envelope.alt_floor_m, envelope.alt_ceiling_m);

  const buildTarget = useCallback(
    (dxPx: number, dyPx: number): ManualFlyTarget => {
      const target = targetFromPointer(
        { lat: drone.latitude, lng: drone.longitude },
        { dxPx, dyPx },
        calibration.metresPerPixel,
        { mapBearingDeg },
      );
      return {
        ...target,
        uncertaintyLabel: gate.uncertaintyLabel,
        uncertaintyM: gate.uncertaintyM,
        altitudeM: targetAltitudeM,
        speedMs: manual.speedCapMs,
      };
    },
    [
      drone.latitude,
      drone.longitude,
      calibration.metresPerPixel,
      mapBearingDeg,
      gate.uncertaintyLabel,
      gate.uncertaintyM,
      targetAltitudeM,
      manual.speedCapMs,
    ],
  );

  // Read from document-level listeners, which outlive their render.
  const longPressStateRef = useRef({
    interactive,
    canFlyToHere: false,
    deadZonePx: ring.deadZonePx,
    focusLocked,
    fixStale: false,
  });
  longPressStateRef.current = {
    interactive,
    canFlyToHere: onFlyTarget !== undefined,
    deadZonePx: ring.deadZonePx,
    focusLocked,
    fixStale: ghost.stale,
  };
  const buildTargetRef = useRef(buildTarget);
  buildTargetRef.current = buildTarget;
  const onRecenterRef = useRef(onRecenter);
  onRecenterRef.current = onRecenter;

  /**
   * Press and hold for 500 ms to drop a target pin; 8 px of movement cancels it
   * — movement beats time, so a press that turns into a vector drag or a map pan
   * never also drops a pin behind it.
   *
   * Listened for on the DOCUMENT rather than on the surface, and without ever
   * calling `preventDefault`, because the surface is `pointer-events: none`
   * outside its four controls and must stay that way: the map underneath has to
   * keep panning normally, and a pan is exactly the movement that cancels this.
   */
  useEffect(() => {
    if (!interactive) return;

    let armed: { pointerId: number; clientX: number; clientY: number } | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    /**
     * Every pointer currently down anywhere on the page.
     *
     * Counted here rather than inferred from the drag refs because listener
     * ORDER must not decide this: the two-finger cancellation runs on the window
     * and clears those refs before this document listener ever sees the second
     * pointer. A second finger is the map's gesture in every case, and a set of
     * ids says so regardless of who ran first.
     */
    const down = new Set<number>();

    const disarm = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      armed = null;
    };

    /**
     * A long press that fired must not ALSO be a click on whatever was
     * underneath — selecting a different aircraft, say. One capture-phase
     * listener, once, swallows exactly that click and nothing else.
     */
    const swallowNextClick = () => {
      const swallow = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
      };
      window.addEventListener('click', swallow, { capture: true, once: true });
      // A press that produces no click at all (a cancelled touch) must not leave
      // the listener armed for the operator's next real click.
      window.setTimeout(() => window.removeEventListener('click', swallow, true), 400);
    };

    const onDown = (event: PointerEvent) => {
      const state = longPressStateRef.current;
      down.add(event.pointerId);
      // A second finger is the map's gesture — a pinch or a two-finger pan — and
      // never a target.
      if (down.size > 1) {
        disarm();
        return;
      }
      // Rule: never competing with a velocity command. While a vector drag or a
      // pin drag is live the long press is disabled entirely.
      if (vectorPointerRef.current !== null || pinPointerRef.current !== null) {
        disarm();
        return;
      }
      if (!state.interactive || !state.canFlyToHere) return;
      if (!event.isPrimary || event.button !== 0) return;
      // A target is a point on the GROUND, worked out from where the aircraft
      // sits on the surface — which is only known while the camera is locked to
      // it. With the camera loose the aircraft may be anywhere, or off screen,
      // and a pin derived from the anchor would name a place nobody chose.
      //
      // Sampled HERE, in the capture phase, rather than when the press fires:
      // this same press is about to bubble into the stage and release the lock
      // (touching the map is how an operator takes the camera), so by 500 ms
      // from now it would always read as unlocked.
      if (!state.focusLocked) return;
      // Same rule, one step further out: a target is `buildTarget`'s stale
      // position plus a pixel offset, so on an old fix the ORIGIN is wrong and
      // the confirm bar's `±N M AT THIS ZOOM` — which is about pixels, not about
      // age — says nothing about it. Of everything drawn from the frozen fix
      // this is the only one that becomes a DISPATCHED command, so it is the one
      // that gets refused. The readout's amber `FIX +Ns` and the live region say
      // why; nothing else on the surface changes.
      if (state.fixStale) return;

      const element = event.target as Element | null;
      // The yaw ring, the altitude knob, STOP and RECENTER are controls with
      // their own meaning; a hold on one of them is not a target.
      if (element?.closest?.('[data-manual-no-target]')) return;
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (
        event.clientX < rect.left
        || event.clientX > rect.right
        || event.clientY < rect.top
        || event.clientY > rect.bottom
      ) {
        return;
      }

      armed = { pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY };
      timer = setTimeout(() => {
        timer = null;
        const press = armed;
        armed = null;
        if (press === null) return;

        const centre = centreOf();
        const dxPx = press.clientX - centre.x;
        const dyPx = press.clientY - centre.y;
        // Inside the grab halo the operator is finding the aircraft symbol, not
        // naming a place to fly to — the same rule the dead zone already states
        // for a drag.
        if (Math.hypot(dxPx, dyPx) <= longPressStateRef.current.deadZonePx) return;

        // Optional and never load-bearing: the pin and the confirm bar are the
        // real cue, and Safari has no `navigator.vibrate` at all.
        vibrate(MANUAL_HAPTIC.pinDropped);
        // A proposal. Nothing reaches the wire until the operator confirms it.
        onFlyTargetRef.current?.(buildTargetRef.current(dxPx, dyPx));
        swallowNextClick();
        // Take the camera back. This press has just been read as "the operator
        // touched the map", which released the lock — and a pin is drawn from
        // the aircraft's position on the surface, so it stays truthful only
        // while the aircraft is held there.
        onRecenterRef.current?.();
      }, MANUAL_LIMITS.LONG_PRESS_MS);
    };

    const onMove = (event: PointerEvent) => {
      // A pointer moving with NO button held is not down, whatever this set
      // still believes — and that is the one proof available here, because a
      // pointer let go outside the document delivers neither `pointerup` nor
      // `pointercancel` to these listeners. One id left behind made
      // `down.size > 1` true for ever after, silently disarming every later
      // long press for the life of the effect: FLY TO HERE stops working with
      // no cue at all, which reads as the pin being broken rather than as a
      // pointer nobody let go of. `buttons` is 1 for a touch or a pen in
      // contact and 0 only for one that is hovering, so a genuine second finger
      // is never pruned by this and the two-finger rule below is untouched.
      if (event.buttons === 0) down.delete(event.pointerId);
      if (armed === null || event.pointerId !== armed.pointerId) return;
      const moved = Math.hypot(event.clientX - armed.clientX, event.clientY - armed.clientY);
      // Movement beats time. Past this the gesture is a vector drag or a map
      // pan, and it stays one.
      if (moved > MANUAL_LIMITS.LONG_PRESS_CANCEL_PX) disarm();
    };

    // `pointercancel` is routine on a tablet — a system gesture, a notification,
    // an incoming call — and takes the identical path: the press is over, so the
    // press is disarmed. Nothing is ever left pending on a pointer that is gone.
    const onUp = (event: PointerEvent) => {
      down.delete(event.pointerId);
      if (armed !== null && event.pointerId === armed.pointerId) disarm();
    };

    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onUp, true);
    return () => {
      disarm();
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };
  }, [interactive, centreOf, surfaceRef]);

  /**
   * Where the pin is DRAWN — from its ground position, never from the pixels it
   * was dropped at. The aircraft drifts under a target; the target does not
   * move with the aircraft.
   */
  const pinOffset = useMemo(
    () =>
      flyTarget === null
        ? null
        : pointerFromTarget(
            { lat: drone.latitude, lng: drone.longitude },
            flyTarget,
            calibration.metresPerPixel,
            { mapBearingDeg },
          ),
    [flyTarget, drone.latitude, drone.longitude, calibration.metresPerPixel, mapBearingDeg],
  );

  const refinePin = useCallback(
    (dxPx: number, dyPx: number) => {
      onFlyTargetRef.current?.(buildTargetRef.current(dxPx, dyPx));
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // The keyboard path to FLY TO HERE.
  //
  // Only a pointer long press could CREATE a target, and GO-TO — the fallback —
  // is refused while manual authority is held, so a keyboard-only operator who
  // was holding the aircraft had no way to command a fly-to at all. `F` drops a
  // pin at the ring's north point (a place the operator can see, one ring radius
  // ahead, never under the halo) and focus moves to it, where the arrow keys
  // already refine and the confirm bar already takes Enter and Escape.
  //
  // Nothing is sent. It is the identical proposal the long press makes.
  // ---------------------------------------------------------------------------
  const pinRef = useRef<HTMLButtonElement | null>(null);
  const focusPinRef = useRef(false);
  const ringRadiusRef = useRef(ring.outerRadiusPx);
  ringRadiusRef.current = ring.outerRadiusPx;
  useEffect(() => {
    if (!interactive || onFlyTarget === undefined || !focusLocked) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== FLY_TO_HERE_KEY) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.repeat) return;
      // The keyboard path to the same proposal, so it takes the same refusal —
      // read from the ref rather than the closure so a fix going stale does not
      // have to re-register this listener.
      if (longPressStateRef.current.fixStale) return;
      const element = event.target as HTMLElement | null;
      // Never a flight verb while the operator is typing somewhere else in the COP.
      if (
        element?.isContentEditable
        || element?.closest?.('input, textarea, select, [contenteditable="true"], [data-cop-text-scope]')
      ) {
        return;
      }
      event.preventDefault();
      focusPinRef.current = true;
      // Screen-up from the aircraft, one ring radius out: 20 m ahead at the
      // calibrated zoom, whatever the surface size.
      refinePin(0, -ringRadiusRef.current);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [interactive, onFlyTarget, focusLocked, refinePin]);

  // The pin only exists once the target round-trips back through the COP, so the
  // focus move waits for the element rather than the keystroke.
  useEffect(() => {
    if (!focusPinRef.current || flyTarget === null) return;
    focusPinRef.current = false;
    pinRef.current?.focus();
  }, [flyTarget]);

  const onPinDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!interactive || !event.isPrimary || event.button !== 0) return;
    capture(event);
    pinPointerRef.current = event.pointerId;
    setPinDragging(true);
  };
  const onPinMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (pinPointerRef.current !== event.pointerId) return;
    const centre = centreOf();
    refinePin(event.clientX - centre.x, event.clientY - centre.y);
  };
  const onPinEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (pinPointerRef.current !== event.pointerId) return;
    releaseCapture();
    pinPointerRef.current = null;
    setPinDragging(false);
  };
  /**
   * The way to refine a pin without a pointer at all (WCAG 2.1.1, Keyboard).
   *
   * Not the 2.5.7 answer, which has to be a POINTER path: that one is a second
   * long press, which re-places the pin outright. Both exist; they are
   * different criteria and different inputs.
   */
  const onPinKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (pinOffset === null) return;
    const step = event.shiftKey ? PIN_NUDGE_COARSE_PX : PIN_NUDGE_PX;
    const nudge: Record<string, [number, number]> = {
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
    };
    const move = nudge[event.key];
    if (!move) return;
    event.preventDefault();
    event.stopPropagation();
    refinePin(pinOffset.dxPx + move[0], pinOffset.dyPx + move[1]);
  };

  const touch = pointer === 'touch';

  return (
    <div
      ref={surfaceRef}
      role="group"
      aria-label={`Manual flight control for ${callsign}`}
      data-testid="manual-map-control"
      data-controllable={ring.controllable ? 'true' : 'false'}
      data-focus-locked={focusLocked ? 'true' : 'false'}
      data-pointer={pointer}
      data-handed={handed}
      data-anchor={anchor.centred ? 'centre' : anchor.stopCorner === 'left' ? 'right' : 'left'}
      // Marks everything inside as a flight control, so the stage can tell a
      // gesture apart from a request to take the camera.
      data-manual-surface=""
      // The container never takes the pointer — only the controls below opt back
      // in, which is what leaves the rest of the map pannable. In the minimap
      // that is exactly wrong: there the surface takes everything it is not
      // handing to a control, and the map under it cannot be panned at all.
      className={
        lockPointer
          ? 'pointer-events-auto absolute z-20 select-none'
          : 'pointer-events-none absolute z-20 select-none'
      }
      // `touch-action: none` stops the browser claiming the drag as a scroll or
      // a pinch before the handlers ever see it; `overscroll-behavior: contain`
      // stops a drag that reaches the edge turning into a page-level pull.
      //
      // The four edges replace `inset-0`. All zero is `inset-0`, which is every
      // classic-shell render and every render in the VIDEO layout.
      style={{
        top: surfaceInsets.top,
        right: surfaceInsets.right,
        bottom: surfaceInsets.bottom,
        left: surfaceInsets.left,
        touchAction: 'none',
        overscrollBehavior: 'contain',
      }}
    >
      {portrait && measured && (
        <div
          data-testid="manual-rotate-prompt"
          className="pointer-events-auto absolute left-1/2 top-1/2 w-[min(22rem,90%)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-status-amber/40 bg-card/95 p-3"
        >
          {/* Announced by the surface's one live region, not here: two regions
              saying the same sentence is the sentence said twice. */}
          <p className="text-[11px] leading-relaxed text-status-amber-fg">
            <span className="block font-bold uppercase tracking-[0.12em]">
              {MANUAL_TOUCH_NOTICE.rotateTitle}
            </span>
            {MANUAL_TOUCH_NOTICE.rotateDetail}
          </p>
        </div>
      )}

      {!portrait && !ring.controllable && measured && (
        <div
          data-testid="manual-display-only"
          className="pointer-events-auto absolute left-1/2 top-1/2 w-[min(20rem,90%)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-status-amber/40 bg-card/95 p-3"
        >
          {/* Same as the rotate prompt: the live region at the bottom of this
              surface is the one that speaks. */}
          <p className="text-[11px] leading-relaxed text-status-amber-fg">
            <span className="block font-bold uppercase tracking-[0.12em]">Display only</span>
            {ring.refusal}
          </p>
        </div>
      )}

      {measured && ring.controllable && !portrait && (
        <svg
          width={surface.widthPx}
          height={surface.heightPx}
          className="absolute inset-0"
          aria-hidden
        >
          {/* Display only. `ringGeometry` computed these radii either way and
              the vector clamps against them either way — the operator has just
              asked for the resting map back. The yaw band below is deliberately
              NOT in here: it is the only pointer path to yaw, and an invisible
              control is not a tidier one. */}
          {showRings && (
            <g data-testid="manual-speed-rings">
            {/* Speed presets, innermost first. Labelled in m/s because that is the
                unit the readout, the envelope and the RC all speak. Outlined,
                like every ring below — see `OutlinedCircle`. */}
            {ring.presetRings.map((preset) => (
              <g key={preset.speedMs}>
                <OutlinedCircle
                  cx={cx}
                  cy={cy}
                  r={preset.radiusPx}
                  strokeWidth={1.5}
                  dash="3 5"
                  className="stroke-manual-ring"
                />
                <text
                  x={cx + 4}
                  y={cy - preset.radiusPx - 3}
                  className="fill-manual-ring font-mono tabular-nums text-[9px]"
                  stroke="var(--manual-ring-outline)"
                  strokeWidth={2}
                  paintOrder="stroke"
                >
                  {preset.speedMs}
                </text>
              </g>
            ))}

            {/* The envelope ring: full deflection, and where the aircraft will be
                in four seconds at max speed. */}
            <OutlinedCircle
              cx={cx}
              cy={cy}
              r={ring.outerRadiusPx}
              strokeWidth={2}
              className="stroke-manual-ring"
            />

            {/* The operator's speed cap, when it is below the envelope. Drawn as
                the wall the vector actually stops at. */}
            {manual.speedCapMs < ring.maxSpeedMs && (
              <OutlinedCircle
                cx={cx}
                cy={cy}
                r={(ring.outerRadiusPx * manual.speedCapMs) / ring.maxSpeedMs}
                strokeWidth={2}
                dash="6 4"
                className="stroke-manual-ring"
              />
            )}

            {/* Inside this a drag is the operator finding the symbol, not a 0.4 m/s
                command. */}
            <OutlinedCircle
              cx={cx}
              cy={cy}
              r={ring.deadZonePx}
              strokeWidth={1.5}
              className="stroke-manual-ring"
            />
            </g>
          )}

          {/* Yaw. `pointer-events: stroke` makes the band itself the target and
              leaves the disc inside it pannable.
              Two circles, and the wider one underneath is not decoration: this
              band is the ONLY pointer path to yaw, so SC 1.4.11 asks for 3:1
              against what it sits on — and what it sits on is an arbitrary
              basemap, including satellite imagery, where no theme surface token
              can promise anything. A dark outline under a light stroke holds on
              all of them. It must not take the pointer, or it would swallow the
              outer half of the band's own hit area. */}
          <circle
            cx={cx}
            cy={cy}
            r={ring.outerRadiusPx + YAW_RING_GAP_PX}
            fill="none"
            strokeWidth={YAW_RING_STROKE_PX + RING_OUTLINE_PX * 2}
            className="stroke-manual-ring-outline"
            style={{ pointerEvents: 'none' }}
          />
          <circle
            cx={cx}
            cy={cy}
            r={ring.outerRadiusPx + YAW_RING_GAP_PX}
            fill="none"
            strokeWidth={YAW_RING_STROKE_PX}
            data-testid="manual-yaw-ring"
            data-manual-no-target=""
            className={
              yawRate !== 0
                ? 'pointer-events-auto stroke-status-amber'
                : 'pointer-events-auto stroke-manual-ring'
            }
            style={{
              pointerEvents: interactive ? 'stroke' : 'none',
              cursor: 'ew-resize',
              strokeOpacity: yawRate !== 0 ? 1 : 0.75,
            }}
            onPointerDown={onYawDown}
            onPointerMove={onYawMove}
            onPointerUp={onYawEnd}
            onPointerCancel={onYawEnd}
            onPointerLeave={onYawEnd}
          />
          {yawRate !== 0 && (
            <path
              d={yawArcPath(cx, cy, ring.outerRadiusPx + YAW_RING_GAP_PX, yawRate)}
              fill="none"
              strokeWidth={YAW_RING_STROKE_PX}
              className="stroke-status-amber"
              style={{ pointerEvents: 'none' }}
            />
          )}

          {/* Where the boundary is along the commanded bearing. */}
          {fenceMarkPx !== null && setpoint.bearingDeg !== null && fenceMarkPx < ring.outerRadiusPx * 3 && (
            <circle
              cx={cx + Math.sin(screenBearingRad) * fenceMarkPx}
              cy={cy - Math.cos(screenBearingRad) * fenceMarkPx}
              r={5}
              fill="none"
              strokeWidth={1.5}
              className="stroke-status-red"
            />
          )}

          {/* The line from the aircraft to a pending target, so the pin is read
              as a leg to fly and not as a mark on the map. */}
          {pinOffset !== null && (
            <line
              x1={cx}
              y1={cy}
              x2={cx + pinOffset.dxPx}
              y2={cy + pinOffset.dyPx}
              strokeWidth={1.5}
              strokeDasharray="4 4"
              className="stroke-status-blue"
            />
          )}

          {/* The raw pointer, still drawn past the clamp. This is the whole
              "show the clamp as it happens" requirement: the commanded vector
              stops and the hand visibly keeps going. */}
          {vectorDrag && (
            <line
              x1={vectorTip.x}
              y1={vectorTip.y}
              x2={cx + vectorDrag.dxPx}
              y2={cy + vectorDrag.dyPx}
              strokeWidth={1}
              strokeDasharray="2 4"
              className="stroke-muted-foreground"
            />
          )}

          {/* The commanded vector. */}
          {vectorLengthPx > 0 && (
            <>
              <line
                x1={cx}
                y1={cy}
                x2={vectorTip.x}
                y2={vectorTip.y}
                strokeWidth={2.5}
                strokeLinecap="round"
                className={
                  setpoint.clamped.length > 0
                    ? 'stroke-status-amber'
                    : 'stroke-status-green'
                }
              />
              <circle
                cx={vectorTip.x}
                cy={vectorTip.y}
                r={5}
                className={
                  setpoint.clamped.length > 0 ? 'fill-status-amber' : 'fill-status-green'
                }
              />
            </>
          )}

          {/* Dead-reckoned position: where the aircraft is NOW, as opposed to
              where its last fix was. Only drawn once it has moved far enough to
              mean anything at this scale. */}
          {ghostPx > 2 && (
            <circle
              cx={cx + Math.sin(ghostRad) * ghostPx}
              cy={cy - Math.cos(ghostRad) * ghostPx}
              r={4}
              fill="none"
              strokeWidth={1.5}
              strokeDasharray="2 2"
              className={ghost.stale ? 'stroke-status-red' : 'stroke-status-blue'}
            />
          )}
        </svg>
      )}

      {/* The grab halo. A real button, so it is focusable, named, and reachable
          without a pointer at all. 44 px across on a mouse, 56 on a finger —
          `ringGeometry` decides, from the pointer mode this surface was given. */}
      {measured && ring.controllable && !portrait && (
        <button
          type="button"
          data-testid="manual-grab-halo"
          disabled={!interactive}
          // "while the map has the keyboard" is not padding: `DroneCommandBar`
          // hands SPACE to the flight keys before its own dialog guard, but
          // returns early for anything inside `[data-cop-text-scope]` — the
          // header drone search. A flat "space to stop" is a promise this
          // surface does not always keep, and the on-map STOP is the one that
          // does.
          aria-label={`Flight vector for ${callsign} — drag to fly, release to hover. Keys W S A D, altitude C Z, yaw Q E, space to stop while the map has the keyboard, 1 2 3 set the speed cap, F or press and hold on the map to place a fly-to target.`}
          aria-describedby="manual-readout"
          onPointerDown={onVectorDown}
          onPointerMove={onVectorMove}
          onPointerUp={onVectorEnd}
          onPointerCancel={onVectorEnd}
          onPointerLeave={onVectorEnd}
          className="pointer-events-auto absolute rounded-full border border-status-green/50 bg-status-green/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            left: cx - ring.deadZonePx,
            top: cy - ring.deadZonePx,
            width: ring.deadZonePx * 2,
            height: ring.deadZonePx * 2,
            touchAction: 'none',
            cursor: vectorDrag ? 'grabbing' : 'grab',
          }}
        />
      )}

      {/* The pending target. Draggable to refine, nudgeable with the arrow keys,
          and it sends NOTHING until the operator confirms. */}
      {measured && ring.controllable && !portrait && flyTarget !== null && pinOffset !== null && (
        <button
          type="button"
          ref={pinRef}
          data-testid="manual-fly-pin"
          data-manual-no-target=""
          aria-label={`Fly-to target for ${callsign} — ${Math.round(flyTarget.distanceM)} metres at ${
            flyTarget.bearingDeg === null ? 'no bearing' : `${Math.round(flyTarget.bearingDeg)} degrees`
          }, ${flyTarget.uncertaintyLabel}. Arrow keys refine, Enter confirms, Escape cancels.`}
          onPointerDown={onPinDown}
          onPointerMove={onPinMove}
          onPointerUp={onPinEnd}
          onPointerCancel={onPinEnd}
          onPointerLeave={onPinEnd}
          onKeyDown={onPinKeyDown}
          className="pointer-events-auto absolute flex items-center justify-center rounded-full border-2 border-status-blue bg-status-blue/20 text-[10px] font-bold text-status-blue-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{
            left: cx + pinOffset.dxPx - (touch ? 28 : 22),
            top: cy + pinOffset.dyPx - (touch ? 28 : 22),
            width: touch ? 56 : 44,
            height: touch ? 56 : 44,
            touchAction: 'none',
            cursor: pinDragging ? 'grabbing' : 'grab',
          }}
        >
          ◎
        </button>
      )}

      {/* Altitude rail. The knob is 44 px and the rail 44 px wide, past the
          contract's 40 px.
          On a mouse it sits at the middle of the right edge, clear of a centred
          ring. On a finger it CANNOT: the ring is anchored in a bottom corner
          with only its edge gutter to spare on that side, so the rail crosses to
          the opposite column — above STOP, under the operator's other thumb.
          One thumb flies; the other holds the two controls that stop a climb.
          Its HEIGHT is `surfaceFurniture`'s answer, not a constant: at the full
          110 px of touch travel this box is 264 px tall and starts 28 px above
          the top of a 316 px minimap, clipping its own UP/CEIL label and the
          full-climb knob position. Short travel on a small surface beats a rail
          whose ends are off screen. */}
      {measured && ring.controllable && !portrait && (
        <div
          data-testid="manual-altitude-rail"
          className={
            touch
              ? `pointer-events-none absolute bottom-20 ${
                  anchor.stopCorner === 'left' ? 'left-6' : 'right-6'
                } flex flex-col items-center`
              : 'pointer-events-none absolute right-6 top-1/2 flex -translate-y-1/2 flex-col items-center'
          }
          style={{ height: furniture.railHeightPx }}
        >
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
            {vertical.clamped === 'ceiling' ? 'CEIL' : 'UP'}
          </span>
          <div className="relative my-1 flex-1 w-11">
            <span
              aria-hidden
              className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-divider"
            />
            <button
              type="button"
              data-testid="manual-altitude-knob"
              data-manual-no-target=""
              disabled={!interactive}
              aria-label={`Altitude rate for ${callsign} — drag up to climb, down to descend. Keys C and Z.`}
              onPointerDown={onRailDown}
              onPointerMove={onRailMove}
              onPointerUp={onRailEnd}
              onPointerCancel={onRailEnd}
              onPointerLeave={onRailEnd}
              className="pointer-events-auto absolute left-1/2 size-11 -translate-x-1/2 rounded-md border border-subtle bg-card/90 font-mono tabular-nums text-[10px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                top: `calc(50% + ${clamp(railOffsetPx, -railTravelPx, railTravelPx)}px)`,
                transform: 'translate(-50%, -50%)',
                touchAction: 'none',
                cursor: 'ns-resize',
              }}
            >
              {vertical.isHover ? '—' : `${vertical.verticalMs > 0 ? '+' : ''}${vertical.verticalMs.toFixed(1)}`}
            </button>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
            {vertical.clamped === 'floor' ? 'FLOOR' : 'DOWN'}
          </span>
        </div>
      )}

      {/* RECENTER. Only exists while the camera is actually loose, so it is
          never a control that does nothing — and it says what the released
          state COSTS rather than just naming it, because the risk is dragging
          from a halo the aircraft symbol has already left. */}
      {manual.holdsAuthority && !focusLocked && onRecenter && (
        <div className="pointer-events-none absolute left-1/2 top-3 flex -translate-x-1/2 flex-col items-center gap-1">
          <button
            type="button"
            data-testid="manual-recenter"
            data-manual-no-target=""
            onClick={onRecenter}
            className="pointer-events-auto flex h-11 min-w-[9rem] items-center justify-center rounded-lg border border-status-amber/50 bg-card/95 px-3 text-[11px] font-bold uppercase tracking-[0.14em] text-status-amber-fg hover:bg-status-amber/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Recenter
          </button>
          {/* Announced by the surface's one live region — a region that appears
              WITH its text often says nothing at all, and two regions racing to
              describe one state is the state said twice. */}
          <span className="rounded bg-card/95 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            Map unlocked — re-locks on its own
          </span>
        </div>
      )}

      {/* STOP. Always visible, always the same place, big enough to hit without
          looking — 144×56 px, past the contract's 120×44. Never disabled while
          authority is held: an emergency stop that is greyed out is not one.
          On a finger it takes the bottom corner the control did NOT, because a
          two-handed grip puts the thumbs there and nowhere near the middle.

          `z-50` for the same reason ManualExitConfirm carries a STOP of its own:
          a control that outranks this one hides it. Below `lg` the drone details
          panel is a `fixed z-40` drawer across the bottom of the viewport, and
          STOP sits at `bottom-4` inside a map whose own bottom edge is 84px up —
          squarely inside the drawer's band. An operator who opened DETAILS mid-
          session to read link quality or satellite count (which is now the only
          place those live) lost the emergency stop behind it. It floats over the
          panel instead. */}
      {manual.holdsAuthority && (
        <button
          type="button"
          data-testid="manual-stop"
          data-manual-no-target=""
          onClick={() => {
            manual.hover();
            // Optional: the vector disappearing and the readout reading HOVER
            // are the cue that has to work everywhere.
            vibrate(MANUAL_HAPTIC.stopped);
          }}
          aria-label={`Stop ${callsign} — hold position, keep manual authority. Space.`}
          className={
            touch
              ? `pointer-events-auto absolute z-50 bottom-4 ${
                  anchor.stopCorner === 'left' ? 'left-4' : 'right-4'
                } flex h-14 min-w-[9rem] flex-col items-center justify-center rounded-lg border border-status-red/50 bg-status-red/15 uppercase text-status-red-fg hover:bg-status-red/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`
              : 'pointer-events-auto absolute z-50 bottom-4 left-1/2 flex h-14 min-w-[9rem] -translate-x-1/2 flex-col items-center justify-center rounded-lg border border-status-red/50 bg-status-red/15 uppercase text-status-red-fg hover:bg-status-red/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          }
        >
          <span className="text-[15px] font-bold tracking-[0.18em]">STOP</span>
          <kbd className="text-[9px] tracking-wider text-muted-foreground">SPACE</kbd>
        </button>
      )}

      {/* The readout. Words for every state, not colours: an operator scanning
          this at 02:00 reads BRG/SPD before they read a hue.

          NOT a live region, deliberately. It re-renders on every pointer move,
          so `aria-live` here queued a fresh announcement several times a second
          and buried the ones that matter — a clamp, an override, a refusal —
          under a stream of bearings. It is `aria-describedby` content for the
          grab halo instead, read on demand, and the discrete changes get their
          own region below.

          WHERE it sits is `surfaceFurniture`'s answer rather than a class pair.
          At minimap scale the bottom-left corner is under the STOP key, and
          this panel is drawn after STOP with an opaque background — so it
          painted over the emergency stop, which stayed clickable and stopped
          being findable. STOP does not move; this does. */}
      {measured && ring.controllable && !portrait && (
        <div
          id="manual-readout"
          data-testid="manual-readout"
          data-placement={furniture.readoutMoved ? 'top' : 'bottom'}
          className="pointer-events-none absolute rounded-lg border border-subtle bg-card/95 px-3 py-2 text-[11px] leading-relaxed text-foreground"
          style={{
            left: furniture.readout.leftPx,
            top: furniture.readout.topPx ?? undefined,
            bottom: furniture.readout.bottomPx ?? undefined,
            maxWidth: furniture.readout.maxWidthPx,
          }}
        >
          {/* Wraps. The first row is ~45 monospace characters, wider than a
              276 px minimap, and an unwrapped row inside an `overflow-hidden`
              panel loses YAW and half of VS with nothing to say it did. */}
          <div className="flex flex-wrap items-center gap-x-3 font-mono tabular-nums tracking-[0.06em]">
            <span className="callsign font-sans">{callsign}</span>
            <span>
              BRG{' '}
              {setpoint.bearingDeg === null
                ? '---'
                : String(Math.round(setpoint.bearingDeg)).padStart(3, '0')}
              °
            </span>
            <span>SPD {setpoint.speedMs.toFixed(1)} m/s</span>
            <span>
              VS {vertical.verticalMs > 0 ? '+' : ''}
              {vertical.verticalMs.toFixed(1)} m/s
            </span>
            <span>
              YAW {yawRate > 0 ? '+' : ''}
              {Math.round(yawRate * envelope.max_yaw_rate_dps)}°/s
            </span>
            {/* The age of the fix EVERY other figure on this line is drawn from,
                and the only one that is a measurement rather than a command.
                Always present, never conditional: a number that appears only
                when it is bad is a number the operator cannot trust to be there,
                and its absence is indistinguishable from a fresh fix. Amber past
                `TELEMETRY_STALE_MS`, the same threshold that refuses to start a
                session on a fix this old. */}
            <span
              data-testid="manual-fix-age"
              className={ghost.stale ? 'font-bold text-status-amber-fg' : undefined}
            >
              FIX +{fixAgeS}s
            </span>
          </div>
          {/* One short line, by request (field testing 2026-08-16): the current
              cap and the one rule that matters mid-drag. The gesture contract
              and the cap keys live in the grab halo's aria-label instead of on
              screen. Warnings still join the line when there is one. */}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
            <span>Release = hover · cap {manual.speedCapMs} m/s</span>
            {commanded.outside && (
              <span className="text-status-amber-fg">Outside keep-in — vector not clamped</span>
            )}
            {clampWords.length > 0 && (
              <span className="text-status-amber-fg">Clamped: {clampWords.join(' · ')}</span>
            )}
          </div>
        </div>
      )}

      {/* The discrete half of the readout, and the only live one.
          Its text changes when a LIMIT changes — never when a bearing does — so
          a screen reader hears "clamped: ceiling" once, when it happens, rather
          than losing it in the pointer stream. */}
      <span data-testid="manual-announcer" role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}

/**
 * A ring stroke with a dark outline under it, so it reads on any basemap.
 *
 * Never takes the pointer: only the yaw ring is interactive, and it draws its
 * own pair so its hit area stays exactly the band an operator can see.
 */
function OutlinedCircle({
  cx,
  cy,
  r,
  strokeWidth,
  className,
  dash,
}: {
  cx: number;
  cy: number;
  r: number;
  strokeWidth: number;
  className: string;
  dash?: string;
}) {
  return (
    <g style={{ pointerEvents: 'none' }}>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        strokeWidth={strokeWidth + RING_OUTLINE_PX * 2}
        strokeDasharray={dash}
        className="stroke-manual-ring-outline"
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        strokeWidth={strokeWidth}
        strokeDasharray={dash}
        className={className}
      />
    </g>
  );
}

/** Compass-style angle of a screen offset: 0 is up, clockwise positive. */
function angleDeg(dx: number, dy: number): number {
  return (Math.atan2(dx, -dy) * 180) / Math.PI;
}

/** Shortest signed difference between two angles, degrees, −180..180. */
function signedAngleDelta(fromDeg: number, toDeg: number): number {
  return ((((toDeg - fromDeg) % 360) + 540) % 360) - 180;
}

/**
 * Carry an accumulated yaw sweep forward by one pointer move.
 *
 * The whole point is that the delta is measured against where the pointer was
 * LAST, not where the gesture began: a single move is never half a turn, so the
 * wrap in `signedAngleDelta` can never bite, and a hand going one way round the
 * ring keeps commanding yaw that way however far round it gets.
 *
 * The TOTAL is clamped, so overshoot past full deflection saturates instead of
 * being banked: the rate answers the hand as soon as it comes back, which is
 * how a rate control has to feel. A total left to run unbounded would leave the
 * first part of every back-sweep dead.
 */
export function advanceYawSweep(sweepDeg: number, fromDeg: number, toDeg: number): number {
  return clamp(
    sweepDeg + signedAngleDelta(fromDeg, toDeg),
    -YAW_FULL_DEFLECTION_DEG,
    YAW_FULL_DEFLECTION_DEG,
  );
}

/** Arc from 12 o'clock through `rate` of a full deflection, for the yaw ring. */
function yawArcPath(cx: number, cy: number, radius: number, rate: number): string {
  const sweepDeg = rate * YAW_FULL_DEFLECTION_DEG;
  const endRad = (sweepDeg * Math.PI) / 180;
  const x = cx + Math.sin(endRad) * radius;
  const y = cy - Math.cos(endRad) * radius;
  const largeArc = Math.abs(sweepDeg) > 180 ? 1 : 0;
  const sweepFlag = sweepDeg >= 0 ? 1 : 0;
  return `M ${cx} ${cy - radius} A ${radius} ${radius} 0 ${largeArc} ${sweepFlag} ${x} ${y}`;
}
